import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import type { QualityCheckIssue } from "@/gen/omnios/v1/accounting_pb";
import { useRunQualityCheck } from "@/hooks/use-accounting";
import { useAccountingContext } from "@/lib/accounting-context";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, Info, Search, XCircle } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/accounting/quality-check")({
	component: QualityCheckPage,
});

const SEVERITY_ICONS: Record<string, React.ReactNode> = {
	error: <XCircle className="h-3.5 w-3.5 text-destructive" />,
	warning: <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />,
	info: <Info className="h-3.5 w-3.5 text-blue-500" />,
};

const SEVERITY_LABELS: Record<string, string> = {
	error: "Ошибка",
	warning: "Предупреждение",
	info: "Информация",
};

const CHECK_TYPE_LABELS: Record<string, string> = {
	unbalanced: "Несбалансированные проводки",
	missing_dimensions: "Отсутствующие аналитики",
	numbering_gaps: "Пропуски в нумерации",
	orphan_accounts: "Счета без плана счетов",
	missing_descriptions: "Проводки без описания",
	duplicate_entries: "Дублирующие проводки",
};

function QualityCheckPage() {
	const { t } = useTranslation("accounting");
	const navigate = useNavigate();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("quality-check");
	const { periodId } = useAccountingContext();
	const runCheck = useRunQualityCheck();

	useBreadcrumbs([
		{
			label: t("quality_check", {
				defaultValue: "Экспресс-проверка",
			}),
		},
	]);

	const [issues, setIssues] = useState<QualityCheckIssue[]>([]);
	const [hasRun, setHasRun] = useState(false);

	const handleRun = useCallback(() => {
		runCheck.mutate(
			{ periodId, checkTypes: [] },
			{
				onSuccess: (res) => {
					setIssues(res.issues);
					setHasRun(true);
				},
			},
		);
	}, [periodId, runCheck]);

	const columnDefs = useMemo<ColDef<QualityCheckIssue>[]>(
		() => [
			{
				field: "severity",
				headerName: "",
				width: 40,
				maxWidth: 40,
				cellRenderer: (p: { value: string }) =>
					SEVERITY_ICONS[p.value] ?? SEVERITY_ICONS.info,
				filter: false,
				sortable: false,
			},
			{
				field: "severity",
				headerName: t("severity", { defaultValue: "Уровень" }),
				width: 120,
				valueFormatter: (p) => SEVERITY_LABELS[p.value as string] ?? p.value,
			},
			{
				field: "checkType",
				headerName: t("check_type", { defaultValue: "Тип проверки" }),
				width: 220,
				valueFormatter: (p) => CHECK_TYPE_LABELS[p.value as string] ?? p.value,
			},
			{
				field: "message",
				headerName: t("entry_description"),
				flex: 1,
				minWidth: 300,
			},
			{
				field: "entryId",
				headerName: t("entry_number"),
				width: 100,
				cellClass:
					"text-primary cursor-pointer hover:underline font-mono text-xs",
				valueFormatter: (p) => (p.value ? (p.value as string).slice(0, 8) : ""),
			},
			{
				field: "accountCode",
				headerName: t("account_code"),
				width: 100,
				cellClass: "font-mono text-xs",
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const onCellClicked = useCallback(
		(e: { colDef: ColDef; data: QualityCheckIssue | undefined }) => {
			if (e.colDef.field === "entryId" && e.data?.entryId) {
				navigate({
					to: "/accounting/journal/$id",
					params: { id: e.data.entryId },
				});
			}
			if (e.colDef.field === "accountCode" && e.data?.accountCode) {
				navigate({
					to: "/accounting/accounts/$id",
					params: { id: e.data.accountCode },
				});
			}
		},
		[navigate],
	);

	const errorCount = issues.filter((i) => i.severity === "error").length;
	const warningCount = issues.filter((i) => i.severity === "warning").length;

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "period",
				content: `${t("period")}: ${periodId}`,
				align: "left" as const,
			},
			...(hasRun
				? [
						{
							id: "result",
							content:
								errorCount === 0 && warningCount === 0
									? t("quality_check_ok", {
											defaultValue: "Ошибок не найдено",
										})
									: `${errorCount} ${t("errors", { defaultValue: "ошибок" })}, ${warningCount} ${t("warnings", { defaultValue: "предупреждений" })}`,
							align: "left" as const,
						},
					]
				: []),
		],
		[periodId, hasRun, errorCount, warningCount, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: `quality_check_${periodId}.csv`,
						})
					}
				>
					CSV
				</Button>
				<Button
					size="sm"
					className="h-9"
					onClick={handleRun}
					disabled={runCheck.isPending}
				>
					{t("run_quality_check", { defaultValue: "Запустить проверку" })}
				</Button>
			</div>
			{/* Result summary */}
			{hasRun && issues.length === 0 && (
				<div className="flex items-center gap-2 px-4 py-2 bg-green-50/50 dark:bg-green-900/10 shrink-0">
					<CheckCircle2 className="h-4 w-4 text-positive" />
					<span className="text-sm font-medium text-positive">
						{t("quality_check_passed", {
							defaultValue: "Все проверки пройдены. Ошибок не обнаружено.",
						})}
					</span>
				</div>
			)}

			{/* Grid */}
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				<AgGridReact<QualityCheckIssue>
					ref={gridRef}
					rowData={issues}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					onGridReady={onGridReady}
					onSortChanged={onStateChanged}
					onFilterChanged={onStateChanged}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					rowHeight={28}
					headerHeight={32}
					animateRows={false}
					getRowId={(p) =>
						`${p.data.checkType}-${p.data.entryId}-${p.data.accountCode}-${p.data.message}`
					}
					loading={runCheck.isPending}
					onCellClicked={onCellClicked}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("quality_check_hint", { defaultValue: "Нажмите «Запустить проверку»" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("quality_check_hint_desc", { defaultValue: "Будут проверены все проводки за текущий период" })}</p></div>`}
				/>
			</div>
		</div>
	);
}
